// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/financeiro/components/MultiCenarioForecast.tsx
//  Projeção de fluxo de caixa em 3 cenários (Pessimista, Realista, Otimista).
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import { format, addDays } from 'date-fns';
import { TrendingUp, TrendingDown, Activity, AlertTriangle, Settings2 } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useFluxoCaixaCenarios } from '@/features/financeiro/hooks/useRelatorios';

const fmtCur = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtCurShort = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return v.toFixed(0);
};

interface MultiCenarioForecastProps {
  dtInicio?: string;
  dtFim?: string;
  agrupamento: 'dia' | 'semana' | 'mes';
}

export function MultiCenarioForecast({ dtInicio, dtFim, agrupamento }: MultiCenarioForecastProps) {
  // Defaults: hoje até hoje + 60 dias
  const today = format(new Date(), 'yyyy-MM-dd');
  const defaultEnd = format(addDays(new Date(), 60), 'yyyy-MM-dd');

  const [pessimistaPerEntradas, setPessimistaPerEntradas] = useState(-20);
  const [pessimistaDiasAtraso, setPessimistaDiasAtraso] = useState(7);
  const [pessimistaPerSaidas, setPessimistaPerSaidas] = useState(5);
  const [otimistaPerEntradas, setOtimistaPerEntradas] = useState(5);
  const [otimistaPerSaidas, setOtimistaPerSaidas] = useState(-5);
  const [paramsAbertos, setParamsAbertos] = useState(false);

  const params = useMemo(() => ({
    dtInicio: dtInicio ?? today,
    dtFim:    dtFim    ?? defaultEnd,
    agrupamento,
    pessimistaPerEntradas,
    pessimistaDiasAtraso,
    pessimistaPerSaidas,
    otimistaPerEntradas,
    otimistaDiasAtraso: 0,
    otimistaPerSaidas,
  }), [
    dtInicio, dtFim, agrupamento, today, defaultEnd,
    pessimistaPerEntradas, pessimistaDiasAtraso, pessimistaPerSaidas,
    otimistaPerEntradas, otimistaPerSaidas,
  ]);

  const { data, isLoading } = useFluxoCaixaCenarios(params);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.realista.periodos.map((p, i) => ({
      label:      p.label,
      pessimista: data.pessimista.periodos[i]?.saldoAcumulado ?? 0,
      realista:   p.saldoAcumulado,
      otimista:   data.otimista.periodos[i]?.saldoAcumulado ?? 0,
    }));
  }, [data]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!data) return null;

  const cenarios = [
    { key: 'pessimista', cenario: data.pessimista, color: 'text-red-600',    bg: 'bg-red-50    dark:bg-red-950/30',    Icon: TrendingDown },
    { key: 'realista',   cenario: data.realista,   color: 'text-blue-600',   bg: 'bg-blue-50   dark:bg-blue-950/30',   Icon: Activity },
    { key: 'otimista',   cenario: data.otimista,   color: 'text-green-600',  bg: 'bg-green-50  dark:bg-green-950/30',  Icon: TrendingUp },
  ] as const;

  return (
    <div className="space-y-6">
      {/* Painel de parâmetros */}
      <Collapsible open={paramsAbertos} onOpenChange={setParamsAbertos}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Settings2 className="h-4 w-4" />
                    Parâmetros dos Cenários
                  </CardTitle>
                  <CardDescription>Ajuste os percentuais e atrasos aplicados às projeções.</CardDescription>
                </div>
                <Badge variant="outline">{paramsAbertos ? 'Recolher' : 'Expandir'}</Badge>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Pessimista */}
                <div className="space-y-3 p-4 rounded-lg border border-red-200 bg-red-50/50 dark:bg-red-950/20">
                  <h4 className="font-semibold text-red-700 flex items-center gap-2">
                    <TrendingDown className="h-4 w-4" />Cenário Pessimista
                  </h4>
                  <div className="space-y-2">
                    <Label className="text-xs">% nas entradas previstas</Label>
                    <Input type="number" value={pessimistaPerEntradas}
                      onChange={(e) => setPessimistaPerEntradas(Number(e.target.value))} className="h-8" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Atraso recebimento (dias)</Label>
                    <Input type="number" value={pessimistaDiasAtraso} min={0}
                      onChange={(e) => setPessimistaDiasAtraso(Number(e.target.value))} className="h-8" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">% nas saídas previstas</Label>
                    <Input type="number" value={pessimistaPerSaidas}
                      onChange={(e) => setPessimistaPerSaidas(Number(e.target.value))} className="h-8" />
                  </div>
                </div>

                {/* Otimista */}
                <div className="space-y-3 p-4 rounded-lg border border-green-200 bg-green-50/50 dark:bg-green-950/20">
                  <h4 className="font-semibold text-green-700 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />Cenário Otimista
                  </h4>
                  <div className="space-y-2">
                    <Label className="text-xs">% nas entradas previstas</Label>
                    <Input type="number" value={otimistaPerEntradas}
                      onChange={(e) => setOtimistaPerEntradas(Number(e.target.value))} className="h-8" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">% nas saídas previstas</Label>
                    <Input type="number" value={otimistaPerSaidas}
                      onChange={(e) => setOtimistaPerSaidas(Number(e.target.value))} className="h-8" />
                  </div>
                  <p className="text-xs text-muted-foreground pt-6">
                    Cenário Realista usa valores e datas exatas (sem ajuste).
                  </p>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* KPIs por cenário */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cenarios.map(({ key, cenario, color, bg, Icon }) => (
          <Card key={key} className={bg}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm font-medium flex items-center gap-2 ${color}`}>
                <Icon className="h-4 w-4" />
                {cenario.nome}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <p className="text-xs text-muted-foreground">Saldo Final Projetado</p>
                <p className={`text-2xl font-bold tabular-nums ${cenario.saldoFinal < 0 ? 'text-red-600' : color}`}>
                  {fmtCur(cenario.saldoFinal)}
                </p>
              </div>
              <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
                <div className="flex justify-between">
                  <span>Entradas previstas:</span>
                  <span className="tabular-nums">{fmtCur(cenario.totalEntradasPrevistas)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Saídas previstas:</span>
                  <span className="tabular-nums">{fmtCur(cenario.totalSaidasPrevistas)}</span>
                </div>
                <div className="flex justify-between font-semibold pt-1">
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Saldo mínimo:
                  </span>
                  <span className={`tabular-nums ${cenario.saldoMinimo < 0 ? 'text-red-600' : ''}`}>
                    {fmtCur(cenario.saldoMinimo)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Gráfico comparativo */}
      <Card>
        <CardHeader>
          <CardTitle>Saldo Acumulado por Cenário</CardTitle>
          <CardDescription>
            Curva de saldo acumulado ao longo do horizonte de projeção
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={fmtCurShort} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v: number) => fmtCur(v)}
                labelStyle={{ color: '#000' }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="pessimista"
                name="Pessimista"
                stroke="#dc2626"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="realista"
                name="Realista"
                stroke="#2563eb"
                strokeWidth={2.5}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="otimista"
                name="Otimista"
                stroke="#16a34a"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Tabela comparativa por período */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhamento por Período</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Período</TableHead>
                <TableHead className="text-right">Pessimista</TableHead>
                <TableHead className="text-right">Realista</TableHead>
                <TableHead className="text-right">Otimista</TableHead>
                <TableHead className="text-right">Δ Otim. - Pess.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chartData.map((row) => {
                const delta = row.otimista - row.pessimista;
                return (
                  <TableRow key={row.label}>
                    <TableCell className="font-medium">{row.label}</TableCell>
                    <TableCell className={`text-right tabular-nums ${row.pessimista < 0 ? 'text-red-600' : ''}`}>
                      {fmtCur(row.pessimista)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {fmtCur(row.realista)}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${row.otimista > 0 ? 'text-green-600' : ''}`}>
                      {fmtCur(row.otimista)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {fmtCur(delta)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
